import React, { useState, useEffect } from "react";
import FormItem from "../../components/ui/FormItem";
import FormActions from "../ui/FormActions";
import FormGrid from "../../components/ui/FormGrid";

import FileUpload from "../common/fileupload/FileUpload";

import PropTypes from "prop-types";


import {
  validateMobile,
  validateEmail,
  validateWebsite,
  validateNumberWithLimit
} from "../../utils/validation";

export default function AgentForm({ initialData = {}, onSubmit, isLoading, onClose = () => {} }) {
  const getDefaultFormData = () => ({
    name: "",
    email: "",
    mobile: "",
    password: "",
    industry: "",
    about_us: "",
    brochure: null,
    crm_webhook_url: "",
    primary_budget_buffer_percent: 10,
    secondary_budget_buffer_percent: 20,
  });

  const [formData, setFormData] = useState(getDefaultFormData());

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData?.id !== undefined) {
      setFormData({
        name: initialData.name || "",
        email: initialData.email || "",
        mobile: initialData.mobile || "",
        password: "",
        brochure: initialData.brochure_url || null,
        about_us: initialData.about_us || "",
        industry: initialData.industry || "",
        crm_webhook_url: initialData.crm_webhook_url || "",
        primary_budget_buffer_percent:
          initialData.primary_budget_buffer_percent ?? 10,
        secondary_budget_buffer_percent:
          initialData.secondary_budget_buffer_percent ?? 20,
      });
      return;
    }
    setFormData(getDefaultFormData());
    setErrors({});
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
  
    if (name === "brochure") {
      const file = files?.[0];
      if (!file) return;
  
      setFormData((prev) => ({
        ...prev,
        brochure: file,
      }));
  
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: null }));
      }
  
    } else if (name === "mobile") {
      if (!validateNumberWithLimit(10)(value)) return;
  
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
  
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: null }));
      }
  
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!validateMobile(formData.mobile))
      newErrors.mobile = "Invalid mobile number";

    if (!validateEmail(formData.email))
      newErrors.email = "Invalid email address";

    if (!validateWebsite(formData.crm_webhook_url))
      newErrors.crm_webhook_url = "Invalid website URL";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    const formDataObj = new FormData();
    formDataObj.append("name", formData.name);
    formDataObj.append("email", formData.email);
    formDataObj.append("mobile", formData.mobile);
    formDataObj.append("password", formData.password);
    formDataObj.append("industry", formData.industry);
    formDataObj.append("about_us", formData.about_us);
    formDataObj.append("crm_webhook_url", formData.crm_webhook_url);
    formDataObj.append("primary_budget_buffer_percent", formData.primary_budget_buffer_percent);
    formDataObj.append("secondary_budget_buffer_percent", formData.secondary_budget_buffer_percent);

    if (formData.brochure instanceof File)
      formDataObj.append("brochure", formData.brochure);

    onSubmit(formDataObj);
  };

  const handleCancel = () => {
    setFormData(getDefaultFormData());
    setErrors({});
    onClose?.();
  };

  return (
    <form onSubmit={handleSubmit} role="form">
      <FormGrid>
        <FormItem
          label="Client Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          error={errors.name}
        />

        <FormItem
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          error={errors.email}
        />



        <FormItem
          label="Mobile"
          name="mobile"
          value={formData.mobile}
          onChange={handleChange}
          error={errors.mobile}
          required
        />

        <FormItem
          label="Industry"
          name="industry"
          value={formData.industry}
          onChange={handleChange}
          error={errors.industry}
          required
        />

        <FormItem
          label="CRM Webhook"
          name="crm_webhook_url"
          value={formData.crm_webhook_url}
          onChange={handleChange}
          error={errors.crm_webhook_url}
        />
        <FormItem
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          error={errors.password}
          onChange={handleChange}
          required
        />

        <FormItem
          label="Primary Budget Buffer %"
          name="primary_budget_buffer_percent"
          type="number"
          error={errors.primary_budget_buffer_percent}
          value={formData.primary_budget_buffer_percent}
          onChange={handleChange}
        />

        <FormItem
          label="Secondary Budget Buffer %"
          name="secondary_budget_buffer_percent"
          type="number"
          error={errors.secondary_budget_buffer_percent}
          value={formData.secondary_budget_buffer_percent}
          onChange={handleChange}
        />


      </FormGrid>
      <FormItem
          label="About Us"
          name="about_us"
          type="textarea"
          value={formData.about_us}
          onChange={handleChange}
          error={errors.about_us}
        />

<FormItem label="Brochure" error={errors.brochure} >
  <div className="d-flex justify-center w-100 h-100 align-center">
        <FileUpload
          name="brochure"
          value={formData.brochure}
          onChange={handleChange}
          error={errors.brochure}
        />
        </div>
      </FormItem>
 

      <FormActions
        isLoading={isLoading}
        onCancel={handleCancel}
        saveText="Save Agent"
      />
    </form>
  );
}

/* ✅ Prop Validation */
AgentForm.propTypes = {
  initialData: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    mobile: PropTypes.string,
    industry: PropTypes.string,
    crm_webhook_url: PropTypes.string,
    primary_budget_buffer_percent: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    secondary_budget_buffer_percent: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }),

  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
};